import { useState, useCallback } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useBrandContext } from "@/contexts/brand-context";
import { usePermissions } from "@/hooks/use-permissions";
import {
  usePieceByCode,
  useApprovePiece,
  useRejectPiece,
  useSubmitPiece,
  useRevisePiece,
  useUpdatePiece,
  useRegenerateMedia,
} from "@/hooks/use-pieces";
import type { ApiPiece } from "@/hooks/use-projects";
import { PieceDetail } from "@/components/pieces/piece-detail";
import { RejectionModal } from "@/components/pieces/rejection-modal";
import { InlineEditor } from "@/components/pieces/inline-editor";
import { PostTypeEditor } from "@/components/composers/PostTypeEditor";
import type { PostType } from "@/hooks/use-post-generation";
import { POST_TYPE_META } from "@/hooks/use-post-generation";
import { getPlatformLabel } from "@/components/ui/platform-icon";
import { Skeleton } from "@/components/ui/skeleton";
import { ArrowLeft } from "lucide-react";

/**
 * Standalone routed piece page (/:brandSlug/pieces/:shortCode) — the target
 * for deep links from the activity feed, palette, notifications and the
 * assistant. Renders the same PieceDetail body as the review slide-over.
 */
export function BrandPieceDetailPage() {
  const { t } = useTranslation("pieces");
  const { shortCode } = useParams<{ shortCode: string }>();
  const { brand } = useBrandContext();
  const { canApprove, canEdit, canSchedule } = usePermissions();
  const navigate = useNavigate();

  const { data: piece, isLoading } = usePieceByCode(brand.id, shortCode!);

  // Rejection modal + inline editing (mirrors Review.tsx wiring)
  const [rejectionOpen, setRejectionOpen] = useState(false);
  const [editingField, setEditingField] = useState<string | null>(null);

  const approveMut = useApprovePiece(brand.id);
  const rejectMut = useRejectPiece(brand.id);
  const submitMut = useSubmitPiece(brand.id);
  const reviseMut = useRevisePiece(brand.id);
  const updateMut = useUpdatePiece(brand.id);
  const regenMediaMut = useRegenerateMedia(brand.id);

  const handleRejectConfirm = useCallback(
    (reason: string) => {
      if (piece) {
        rejectMut.mutate(
          { pieceId: piece.id, reason },
          { onSuccess: () => setRejectionOpen(false) },
        );
      }
    },
    [piece, rejectMut],
  );

  const handleSaveField = useCallback(
    (value: string | string[]) => {
      if (!piece || !editingField) return;
      const fields: Partial<Pick<ApiPiece, "copy" | "caption" | "hashtags">> = {};
      (fields as Record<string, string | string[]>)[editingField] = value;
      updateMut.mutate(
        { pieceId: piece.id, fields },
        { onSuccess: () => setEditingField(null) },
      );
    },
    [piece, editingField, updateMut],
  );

  if (isLoading) {
    return (
      <div className="px-6 py-8 max-w-[560px] mx-auto w-full space-y-4">
        <Skeleton className="h-6 w-48" />
        <Skeleton className="aspect-video w-full rounded-lg" />
        <Skeleton className="h-32 w-full" />
      </div>
    );
  }

  if (!piece) {
    return (
      <div className="px-6 py-8 max-w-[560px] mx-auto w-full">
        <p className="text-muted-foreground">{t("pieces:page.notFound")}</p>
        <Link to={`/${brand.slug}`} className="mt-2 inline-block text-sm text-brand hover:underline">
          {t("pieces:page.backToProjects")}
        </Link>
      </div>
    );
  }

  // Edit content (lifted from Review.tsx): PostType-aware pieces get the full
  // PostTypeEditor; legacy pieces fall through to the single-field InlineEditor.
  const editContent = editingField ? (
    piece.postType ? (
      <PostTypeEditor
        brandId={brand.id}
        postType={piece.postType as PostType}
        initial={{
          caption: piece.caption ?? "",
          title: piece.title ?? null,
          description: piece.description ?? null,
          hashtags: piece.hashtags ?? [],
          threadItems: (piece.threadItems as any) ?? null,
          platformKnobs: (piece.platformKnobs as any) ?? {},
          mediaUrls: piece.mediaUrls ?? [],
        }}
        onSave={async (changes) => {
          await new Promise<void>((resolve, reject) => {
            updateMut.mutate(
              { pieceId: piece.id, fields: changes as any },
              {
                onSuccess: () => {
                  setEditingField(null);
                  resolve();
                },
                onError: (err) => reject(err),
              },
            );
          });
        }}
        onRegenerateMedia={() => regenMediaMut.mutateAsync(piece.id).then(() => undefined)}
        regenerateMediaPending={regenMediaMut.isPending}
        brandHandle={brand.slug}
        brandName={brand.name}
        disabled={updateMut.isPending}
      />
    ) : (
      <InlineEditor
        field={editingField as "copy" | "caption" | "hashtags"}
        value={
          editingField === "hashtags"
            ? piece.hashtags
            : (piece as unknown as Record<string, unknown>)[editingField] as string ?? ""
        }
        onSave={handleSaveField}
        onCancel={() => setEditingField(null)}
        isSaving={updateMut.isPending}
      />
    )
  ) : null;

  const typeLabel =
    piece.postType && POST_TYPE_META[piece.postType as PostType]
      ? `${getPlatformLabel(POST_TYPE_META[piece.postType as PostType].platform)} ${POST_TYPE_META[piece.postType as PostType].label}`
      : `${getPlatformLabel(piece.platform)} ${piece.format.toLowerCase()}`;

  return (
    <div className="px-6 py-8 max-w-[560px] mx-auto w-full">
      <div className="flex items-center gap-2">
        <Link
          to={`/${brand.slug}/projects/${piece.projectId}`}
          className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground"
        >
          <ArrowLeft size={14} /> {t("pieces:page.project")}
        </Link>
        <span className="text-muted-foreground">/</span>
        <h1 className="text-sm font-medium">
          <span className="font-mono">{piece.shortCode}</span>
          <span className="mx-1.5 text-muted-foreground">·</span>
          <span>{typeLabel}</span>
        </h1>
      </div>

      <PieceDetail
        piece={piece}
        onApprove={(pieceId) => approveMut.mutate(pieceId)}
        onReject={() => setRejectionOpen(true)}
        onSubmit={(pieceId) => submitMut.mutate(pieceId)}
        onRevise={(pieceId) => reviseMut.mutate(pieceId)}
        onSchedule={() => navigate(`/${brand.slug}/calendar`)}
        canApprove={canApprove}
        canEdit={canEdit}
        canSchedule={canSchedule}
        editingField={editingField}
        onEditField={setEditingField}
        editContent={editContent}
        className="mt-6 space-y-4"
      />

      <RejectionModal
        isOpen={rejectionOpen}
        onClose={() => setRejectionOpen(false)}
        onReject={handleRejectConfirm}
        shortCode={piece.shortCode}
        isLoading={rejectMut.isPending}
      />
    </div>
  );
}
